<template>
  <div class="select-time">
    <div class="select-time__main-slot">
      <slot />
    </div>
    <div class="select-time__toggle">
      <div class="select-time__col">
        <div
          class="select-time__toggle-button"
          :class="{
            'disabled': isDisabledMorning,
            'selected': dayIntervalType === 'morning',
          }"
          @click="selectDayInterval('morning')"
        >
          <span>Утро</span>
          <sprite
            v-if="isDisabledEvening"
            src="sprites_lock"
            class="select-time__lock"
          />
        </div>
      </div>
      <div class="select-time__col">
        <div
          class="select-time__toggle-button"
          :class="{
            'disabled': isDisabledEvening,
            'selected': dayIntervalType === 'evening',
          }"
          @click="selectDayInterval('evening')"
        >
          <span>Вечер</span>
          <sprite
            v-if="isDisabledMorning"
            src="sprites_lock"
            class="select-time__lock"
          />
        </div>
      </div>
    </div>

    <div
      v-if="getCurrentTimeIntervals"
      class="select-time__select-time-container"
    >
      <div
        v-for="(interval, intervalIndex) in getCurrentTimeIntervals"
        :key="`interval-index-${intervalIndex}`"
        class="select-time__select-time-item"
      >
        <div
          class="select-time__select-time-button"
          :class="{
            'selected': isCurrentTimeInterval(`${interval.from}-${interval.to}`)
          }"
          @click="selectTimeInterval(interval)"
        >
          {{ interval.from }}-{{ interval.to }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'SelectTime',

  props: {
    timeIntervals: {
      type: Object,
      required: false,
      default () {
        return {}
      },
    },
  },

  emits: [
    'selectNewTimeInterval',
    'autoSelectNewTimeInterval',
  ],

  data () {
    return {
      dayIntervalType: 'morning',
      from: '',
      to: '',
    }
  },

  computed: {
    isDisabledEvening () {
      return this.dayIntervalType === 'none' || !this.getEveningTimeIntervals.length
    },

    isDisabledMorning () {
      return this.dayIntervalType === 'none' || !this.getMorningTimeIntervals.length
    },

    getEveningTimeIntervals () {
      if (!Object.prototype.hasOwnProperty.call(this.timeIntervals, 'evening')) {
        return []
      }

      const {
        evening = [],
      } = this.timeIntervals

      return evening
    },

    getMorningTimeIntervals () {
      if (!Object.prototype.hasOwnProperty.call(this.timeIntervals, 'morning')) {
        return []
      }

      const {
        morning = [],
      } = this.timeIntervals

      return morning
    },

    getCurrentTimeIntervals () {
      return this.timeIntervals[this.dayIntervalType]
    },
  },

  watch: {
    'timeIntervals' () {
      this.setDayInterval()
      this.setTimeInterval()
      this.selectTimeInterval({
        from: this.from,
        to: this.to,
      }, true)
    },
  },

  mounted () {
    this.setDayInterval()
    this.setTimeInterval()
    this.selectTimeInterval({
      from: this.from,
      to: this.to,
    }, true)
  },

  methods: {
    selectDayInterval (value) {
      if (this.hasDayInterval(value)) {
        this.dayIntervalType = value
      }
    },

    selectTimeInterval (value, isAuto = false) {
      const {
        from = '',
        to = '',
      } = value

      this.from = from
      this.to = to

      this.$emit(
        isAuto
          ? 'autoSelectNewTimeInterval'
          : 'selectNewTimeInterval'
        , {
          from: this.from,
          to: this.to,
          type: this.dayIntervalType,
        })
    },

    hasDayInterval (value) {
      return Object.prototype.hasOwnProperty.call(this.timeIntervals, value)
    },

    setDayInterval () {
      if (!Object.prototype.hasOwnProperty.call(this.timeIntervals, this.dayIntervalType)) {
        if (Object.prototype.hasOwnProperty.call(this.timeIntervals, 'morning')) {
          this.dayIntervalType = 'morning'
        } else if (Object.prototype.hasOwnProperty.call(this.timeIntervals, 'evening')) {
          this.dayIntervalType = 'evening'
        } else {
          this.dayIntervalType = 'none'
        }
      }
    },

    setTimeInterval () {
      if (this.dayIntervalType !== 'none') {
        if (this.timeIntervals[this.dayIntervalType].length > 0) {
          const {
            from = '',
            to = '',
          } = this.timeIntervals[this.dayIntervalType][0]

          this.from = from
          this.to = to
        }
      }
    },

    isCurrentTimeInterval (value) {
      return value === `${this.from}-${this.to}`
    },
  },
}
</script>

<style lang="scss">
$prefix: 'select-time';

.#{$prefix} {
  width: 100%;
}

.#{$prefix}__main-slot {
  margin-bottom: 0.24rem;
}

.#{$prefix}__toggle {
  @include row;

  margin: 0 0.68rem 0.24rem 0.68rem;

  background: map-get($colors, 'grey_4');

  border-radius: 18px;
}

.#{$prefix}__col {
  @include columns(2, 1);
}

.#{$prefix}__toggle-button {
  @include typography('b2', 'bold');

  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  justify-content: center;
  align-items: center;

  padding: 0.06rem 0;
  margin: 2px;

  color: map-get($colors, 'gray_60');

  border-radius: 18px;

  cursor: pointer;

  &:hover {
    color: map-get($colors, 'black');
  }

  &.disabled {
    color: map-get($colors, 'grey_20');
  }

  &.selected {
    background: map-get($colors, 'white');
    color: map-get($colors, 'default');
  }
}

.#{$prefix}__lock {
  margin-left: 0.07rem;

  width: 0.12rem;
  height: 0.12rem;

  color: map-get($colors, 'default');
}

.#{$prefix}__select-time-container {
  @include row;
  @include margin-x(-0.04rem);
  @include margin-y(-0.04rem);
}

.#{$prefix}__select-time-item {
  @include columns(3, 1);
}

.#{$prefix}__select-time-button {
  @include typography('b2', 'medium');
  text-align: center;

  margin: 0.04rem;
  padding: 0.10rem 0;

  border: 1px solid map-get($colors, 'gray_eb');
  border-radius: 12px;

  color: map-get($colors, 'gray_60');

  cursor: pointer;

  &:hover {
    color: map-get($colors, 'black');

    border: 1px solid map-get($colors, 'grey_20');
  }

  &.selected {
    @include typography('b2', 'bold');

    background: map-get($colors, 'default');
    color: map-get($colors, 'white');

    border: 1px solid map-get($colors, 'default');
  }
}
</style>
